import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";

const SUGGESTIONS = [
  "What fabrics do you offer?",
  "How does sampling work?",
  "Which markets do you ship to?",
];

const getSessionId = () => {
  let id = localStorage.getItem("pa-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("pa-chat-session", id);
  }
  return id;
};

const renderText = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Our export desk is momentarily unavailable. Please write to contact@painternational.in.",
        };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gold text-navy shadow-[0_10px_40px_rgba(7,22,64,0.45)] transition-transform duration-300 hover:scale-110"
        aria-label="Open export desk chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-50 flex h-[520px] max-h-[70vh] w-[92vw] max-w-sm flex-col overflow-hidden border border-gold/30 bg-white shadow-[0_24px_80px_rgba(7,22,64,0.35)] sm:right-6"
          >
            <div className="bg-navy px-5 py-4">
              <p className="font-serif text-lg font-semibold text-white">Export Desk Concierge</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-champagne">P.A. International · replies instantly</p>
            </div>

            <div data-testid="chat-message-list" ref={listRef} className="flex-1 space-y-4 overflow-y-auto bg-cream/60 px-4 py-4">
              {messages.length === 0 && (
                <div className="pt-4">
                  <p className="px-1 font-serif text-base italic text-slate-600">
                    Ask us anything about our fabrics, sampling, or shipping.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        data-testid={`chat-suggestion-${s.split(" ")[1].toLowerCase()}`}
                        onClick={() => send(s)}
                        className="border border-navy/15 bg-white px-3 py-2 text-xs text-navy transition-colors hover:border-gold hover:text-gold"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-navy text-white" : "border border-navy/10 bg-white text-slate-700"
                    }`}
                  >
                    {renderText(m.content) || (busy && i === messages.length - 1 ? <span className="animate-pulse text-gold">…</span> : null)}
                  </div>
                </div>
              ))}
            </div>

            <form
              className="flex items-center gap-2 border-t border-navy/10 bg-white p-3"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask the export desk…"
                className="flex-1 border border-navy/15 px-3 py-2.5 text-sm text-navy outline-none placeholder:text-slate-400 focus:border-gold"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={busy}
                className="flex h-10 w-10 items-center justify-center bg-gold text-navy transition-colors hover:bg-champagne disabled:opacity-50"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
